import Alpine from 'alpinejs';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

const CLUSTER_COLOR = '#FF6E29';

/**
 * Custom renderer for MarkerClusterer.
 * Replaces the default blue circles with an accent-coloured SVG.
 */
const clusterRenderer = {
	render( { count, position } ) {
		const size = count < 10 ? 40 : count < 100 ? 48 : 56;
		const svg = [
			`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`,
			`<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${CLUSTER_COLOR}" opacity="0.25"/>`,
			`<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 6}" fill="${CLUSTER_COLOR}"/>`,
			`<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" fill="#ffffff" font-family="Montserrat, sans-serif" font-size="${size < 48 ? 13 : 14}" font-weight="600">${count}</text>`,
			'</svg>',
		].join( '' );

		return new google.maps.Marker( {
			position,
			icon: {
				url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent( svg ),
				scaledSize: new google.maps.Size( size, size ),
				anchor: new google.maps.Point( size / 2, size / 2 ),
			},
			// Ensure clusters sort above individual markers.
			zIndex: 1000 + count,
		} );
	},
};

const MAP_STYLES = [
	{ featureType: 'water', elementType: 'geometry', stylers: [ { color: '#e9e9e9' }, { lightness: 17 } ] },
	{ featureType: 'landscape', elementType: 'geometry', stylers: [ { color: '#FAF4F0' }, { lightness: 20 } ] },
	{ featureType: 'road.highway', elementType: 'geometry.fill', stylers: [ { color: '#ffffff' }, { lightness: 17 } ] },
	{ featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [ { color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 } ] },
	{ featureType: 'road.arterial', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 18 } ] },
	{ featureType: 'road.local', elementType: 'geometry', stylers: [ { color: '#ffffff' }, { lightness: 16 } ] },
	{ featureType: 'poi', elementType: 'geometry', stylers: [ { color: '#FAF4F0' }, { lightness: 21 } ] },
	{ featureType: 'poi.park', elementType: 'geometry', stylers: [ { color: '#dedede' }, { lightness: 21 } ] },
	{ elementType: 'labels.text.stroke', stylers: [ { visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 } ] },
	{ elementType: 'labels.text.fill', stylers: [ { saturation: 36 }, { color: '#333333' }, { lightness: 40 } ] },
	{ elementType: 'labels.icon', stylers: [ { visibility: 'off' } ] },
	{ featureType: 'transit', elementType: 'geometry', stylers: [ { color: '#f2f2f2' }, { lightness: 19 } ] },
	{ featureType: 'administrative', elementType: 'geometry.fill', stylers: [ { color: '#fefefe' }, { lightness: 20 } ] },
	{ featureType: 'administrative', elementType: 'geometry.stroke', stylers: [ { color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 } ] },
];

/**
 * Register the locationMap Alpine component.
 * This must run before Alpine.start() is called.
 */
document.addEventListener( 'alpine:init', () => {
	Alpine.data( 'locationMap', ( config ) => ( {
		open: false,
		active: {},
		map: null,
		config: config,

		init() {
			if ( typeof google !== 'undefined' && google.maps ) {
				this.initMap();
			} else {
				// Google Maps will call this callback once it has loaded.
				window.initGoogleMap = () => this.initMap();
			}
		},

		initMap() {
			this.map = new google.maps.Map( this.$refs.mapCanvas, {
				center: { lat: 0, lng: 0 }, // overridden by fitBounds
				zoom: 2,
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: false,
				styles: MAP_STYLES,
			} );

			const bounds = new google.maps.LatLngBounds();

			const icon = this.config.pinIcon ? {
				url: this.config.pinIcon,
				scaledSize: new google.maps.Size( 40, 40 ),
				anchor: new google.maps.Point( 20, 40 ),
			} : null;

			const markers = this.config.locations.map( ( loc ) => {
				const position = { lat: loc.lat, lng: loc.lng };

				const marker = new google.maps.Marker( {
					position,
					title: loc.title,
					...( icon && { icon } ),
				} );

				// Only individual pins open the panel — cluster clicks just zoom in.
				marker.addListener( 'click', () => {
					this.active = loc;
					this.open = true;
				} );

				bounds.extend( position );

				return marker;
			} );

			new MarkerClusterer( { map: this.map, markers, renderer: clusterRenderer } );

			// Fit all pins into view.
			this.map.fitBounds( bounds );

			// Prevent over-zooming when there is only one pin.
			google.maps.event.addListenerOnce( this.map, 'bounds_changed', () => {
				if ( this.map.getZoom() > 15 ) {
					this.map.setZoom( 15 );
				}
			} );
		},
	} ) );
} );
